import type {
  RevenueAnalysisAvailableRange,
  RevenueAnalysisWindow,
} from '@/shared/api/modules/facilities';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

/**
 * `customParseFormat` is not registered in this app, so a bare `YYYY-MM` string
 * would go through Date's ISO path and land on UTC midnight — which reads as the
 * *previous* month everywhere west of Greenwich. Pinning the day keeps it local.
 */
const asDate = (month: string) => dayjs(`${month}-01`);

const monthLabel = (month: string) => asDate(month).format('MMM YYYY');

const shift = (month: string, months: number) =>
  asDate(month).add(months, 'month').format('YYYY-MM');

/**
 * The chart's own 12-month window (ST #175) — deliberately independent of the
 * global dashboard date filter.
 *
 * `endMonth` is null until the user pages, meaning "whatever the newest month
 * with data is". Keeping it null rather than seeding it from the first response
 * is what makes the window follow new data without a second render, and what
 * makes "am I at the latest window?" a question about the data, not about state.
 *
 * State only, no persistence: nothing in the card text asks the window to
 * survive a tab switch, and resetting to "Last 12 months" is the least
 * surprising thing to come back to.
 */
export const useMonthWindow = () => {
  const [endMonth, setEndMonth] = useState<string | null>(null);

  const describe = useCallback(
    (
      window: RevenueAnalysisWindow | undefined,
      availableRange: RevenueAnalysisAvailableRange | undefined
    ) => {
      const earliest = availableRange?.earliest ?? null;
      const latest = availableRange?.latest ?? null;
      const from = window?.from ?? null;
      const to = window?.to ?? null;

      const canGoBack = !!from && !!earliest && from > earliest;
      const canGoForward = !!to && !!latest && to < latest;

      // "Last 12 months" only while the window still ends on the newest data;
      // once it doesn't, the months have to be named or the label would lie.
      const label =
        !from || !to
          ? 'Last 12 months'
          : to === latest
            ? 'Last 12 months'
            : `${monthLabel(from)} – ${monthLabel(to)}`;

      return {
        label,
        canGoBack,
        canGoForward,
        goBack: () => to && canGoBack && setEndMonth(shift(to, -1)),
        goForward: () => to && canGoForward && setEndMonth(shift(to, 1)),
      };
    },
    []
  );

  return { endMonth, describe };
};
